import { Navigate, Route, Routes } from 'react-router-dom';
import { Suspense, lazy } from 'react';

import AppLayout from 'layouts/app-layout';
import AuthLayout from 'layouts/auth-layout';
import Guarded from 'components/shared-components/Guarded';
import Loading from 'components/shared-components/Loading';
import { NotificationContainer } from 'react-notifications';
import { Organization } from './auth-views/components/SelectOrg';
import RouterErrorElement from 'components/shared-components/RouterErrorElement';
import UserStore from 'mobx/UserStore';
import _ from 'lodash';
import { observer } from 'mobx-react';

const Launchpad = lazy(() => import('./app-views/launchpad'));
const Personnel = lazy(() => import('./app-views/personnel'));
const Setting = lazy(() => import('./app-views/SettingGlobals'));
const SettingProfile = lazy(() => import('./app-views/SettingProfile'));
const Notification = lazy(() => import('./app-views/Notification'));
const IncidentManagement = lazy(() => import('./app-views/incident-management'));
const ForgetPassword = lazy(() => import(`./auth-views/authentication/forgetpassword`));
const Login = lazy(() => import(`./auth-views/authentication/login`));
const Login2FA = lazy(() => import(`./auth-views/authentication/login-2fa`));
const OnlineScamRoute = lazy(() => import(`./app-views/online-scam`));
const CaseDocumentPage = lazy(() => import('./case-document'));

export const Views = (props) => {
  const accessToken = UserStore.accessAuthen?.access_token || null;

  const isAuthenticated = accessToken !== null;

  const { selectBranch, isGlobal } = UserStore;

  const getRedirectRoute = () => {
    try {
      const redirectOrg = localStorage.getItem('redirectOrg');
      const loginPath = redirectOrg ? `/auth/login?org=${redirectOrg}` : '/auth/login';

      if (isAuthenticated) {
        if (_.isEmpty(selectBranch?.chooseBranch)) {
          return isGlobal ? '/auth/organization' : loginPath;
        } else {
          return '/app/launchpad';
        }
      }

      return loginPath;
    } catch (error) {
      return '/';
    }
  };

  const toRedirect = () => {
    if (!isAuthenticated || _.isEmpty(selectBranch?.chooseBranch)) {
      const to = getRedirectRoute();
      return <Navigate to={to} replace />;
    }
    return <AppLayout {...props} />;
  };

  return (
    <>
      <Suspense fallback={<Loading cover="content" />}>
        <Routes>
          <Route errorElement={<RouterErrorElement />} path={`/auth/*`} element={<AuthLayout />}>
            <Route errorElement={<RouterErrorElement />} path={`login`} element={<Login />} />
            <Route errorElement={<RouterErrorElement />} path={`organization`} element={<Organization />} />
            <Route errorElement={<RouterErrorElement />} path={`login-2fa`} element={<Login2FA />} />
            <Route errorElement={<RouterErrorElement />} path={`forget-password`} element={<ForgetPassword />} />
            <Route errorElement={<RouterErrorElement />} path="*" element={<Navigate to="login" replace />} />
          </Route>

          <Route errorElement={<RouterErrorElement />} path={`/app/*`} element={toRedirect()}>
            <Route errorElement={<RouterErrorElement />} path="launchpad" element={<Launchpad />} />

            <Route
              path="incident-management/*"
              element={
                <Guarded
                  // query={{ group: canUpdate ? 'queue_management' : 'incident_management' }}
                  query={{ group: 'Incident Management' }}
                  onNoPermission={<Navigate to={getRedirectRoute()} />}
                >
                  <IncidentManagement />
                </Guarded>
              }
            />

            <Route
              path="personnel/*"
              element={
                <Guarded query={{ group: 'Personnel' }} onNoPermission={<Navigate to={getRedirectRoute()} />}>
                  <Personnel />
                </Guarded>
              }
            />

            <Route
              path="setting/*"
              element={
                <Guarded
                  query={{ group: 'System Administration' }}
                  onNoPermission={<Navigate to={getRedirectRoute()} />}
                >
                  <Setting />
                </Guarded>
              }
            />

            <Route errorElement={<RouterErrorElement />} path="setting-profile/*" element={<SettingProfile />} />

            <Route errorElement={<RouterErrorElement />} path="notification" element={<Notification />} />

            <Route
              path="online-scam/*"
              element={
                <Guarded query={{ group: 'Online Scam' }} onNoPermission={<Navigate to={getRedirectRoute()} />}>
                  <OnlineScamRoute />
                </Guarded>
              }
            />
            <Route errorElement={<RouterErrorElement />} path="*" element={<Navigate to="launchpad" replace />} />
          </Route>

          <Route errorElement={<RouterErrorElement />} path="/case-document" element={<CaseDocumentPage />} />
          <Route errorElement={<RouterErrorElement />} path="/case-document/:session" element={<CaseDocumentPage />} />

          <Route errorElement={<RouterErrorElement />} path="/*" element={<Navigate to={'/app'} replace />} />
        </Routes>
      </Suspense>
      <NotificationContainer />
    </>
  );
};

export default observer(Views);
